// The full-screen video viewer for phones (touch devices), also opened with a
// mouse from a chat's attached videos. Gestures asked for by the user: long
// press plays at 2x, swipe up turns the video (again to undo), double tap
// skips 10 s, tap pauses / plays. The controls go away as soon as it plays
// (the person, 2026-10-30: they were in the way); a tap, or moving the mouse,
// brings them back for a while.
//
// Built like the image viewer (file-lightbox.tsx):
// - Not drawn inside the node view. The pressed element dispatches a
//   CustomEvent; the host mounted next to the editor or the static body
//   (`MediaLayers`) draws it. One signal is taken by one host only (`claim`)
// - Portaled to body (out of any transformed box; the theme is on <html>)
//
// What differs:
// - Black ground (a place to watch video; the theme's ground would melt the
//   video's edges into a light theme)
// - Finger gestures are the state machine in video-gestures.ts; timers are set here
// - Turning rotates the stage holding the video and the controls 90° in CSS.
//   The device orientation is not changed (iPhone Safari cannot lock it)
// - Playback starts synchronously inside the opening tap. The host renders
//   with `flushSync` in the signal's listener and calls `play()` on the new
//   <video> right there: iPhone Safari refuses sound playback outside a user
//   gesture, and once an element has played inside one, later `play()` calls
//   from timers pass too. If still refused, it stays paused showing ▶
// - Full screen with the browser UI gone on touch devices (user request).
//   Where the Fullscreen API works (Android Chrome, iPad) the panel requests
//   `requestFullscreen({ navigationUI: "hide" })` inside the opening tap. With
//   a mouse it stays inside the window (the person, 2026-10-30: the desktop
//   app's window went full screen). In
//   full screen only the panel's descendants are visible, so the menus are
//   drawn inside it (no portals). When the system leaves full screen (Android
//   back) the viewer closes too. iPhone WebKit supports full
//   screen only for <video> (`webkitEnterFullscreen`), so there a "full screen
//   in the native player" button is added; the native player has its own
//   controls and our gestures do not apply there.
// - In an app that shows videos itself (`ShellHost.viewer`, the iOS app's
//   own player) nothing is drawn: the video goes to the app (app-viewer.ts),
//   and the position it closed at comes back to the inline player.
import { m } from "@trameria/i18n";
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal, flushSync } from "react-dom";
import { MeAppContext } from "../../../app/context";
import { useOutsideCover } from "../../../shell/outside/cover";
import { useStowHostPartsWhile } from "../../../shell/phone";
import { Icon } from "../../../ui/icon";
import { useCoarsePointer } from "../pointer";
import { canHand, handToApp } from "./app-viewer";
import { MEDIA_LAYER_Z } from "./file-lightbox";
import { FloatingIconButton } from "./floating-icon-button";
import { claim, returnFocusTo } from "./focus";
import { clampTime, formatVideoTime, VIDEO_SPEEDS } from "./video-controls";
import {
  GESTURE,
  type GestureOutput,
  toViewPoint,
  VideoGestures,
} from "./video-gestures";
import { FullscreenIcon, PauseIcon, PlayIcon } from "./video-icons";
import { SeekBar } from "./video-seek-bar";

export const VIDEO_VIEWER_EVENT = "trameria:video-viewer";

export interface VideoViewerDetail {
  src: string;
  name: string;
  download?: string;
  /** Where the inline player stood (seconds). */
  startAt: number;
  /** Hands the position on close back to the inline player. */
  onClose?: (time: number) => void;
}

export function openVideoViewer(
  from: Element,
  detail: VideoViewerDetail,
): void {
  from.dispatchEvent(
    new CustomEvent<VideoViewerDetail>(VIDEO_VIEWER_EVENT, {
      detail,
      bubbles: true,
    }),
  );
}

interface Open extends VideoViewerDetail {
  opener: Element | null;
}

type WebkitVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

/** The host of the video viewer. Mounted once per screen by `MediaLayers`. */
export function VideoViewer() {
  const [open, setOpen] = useState<Open | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const coarse = useCoarsePointer();
  // Read inside the open signal's listener (set up once).
  const coarseRef = useRef(coarse);
  coarseRef.current = coarse;
  const host = useContext(MeAppContext)?.extensions.shellHost ?? null;
  const hostRef = useRef(host);
  hostRef.current = host;
  /** Escape, as the stage decides it (close a menu first, else the viewer). */
  const onEscape = useRef<(() => void) | null>(null);
  const close = useCallback(() => setOpen(null), []);
  useStowHostPartsWhile(open !== null);
  // The black ground over the host's parts too (a press there does nothing).
  useOutsideCover(open !== null, 1, null);

  // frontend-development: not a fetch; listens for the open signal on window.
  useEffect(() => {
    const onOpen = (e: Event) => {
      if (!claim(e)) return;
      const detail = (e as CustomEvent<VideoViewerDetail>).detail;
      if (!detail) return;
      const opener = e.target instanceof Element ? e.target : null;
      const shellHost = hostRef.current;
      if (canHand(shellHost, detail.src)) {
        handToApp(shellHost, {
          kind: "video",
          items: [{ ...detail, el: opener }],
          index: 0,
          startAt: detail.startAt,
          opener,
          onClosed: (time) => {
            if (time !== undefined) detail.onClose?.(time);
          },
        });
        return;
      }
      // Called synchronously inside the opener's click. Render to the end and
      // call play() right here (waiting for loadedmetadata would be after the
      // network, and iOS would refuse it as outside the gesture).
      flushSync(() =>
        setOpen({
          ...detail,
          opener,
        }),
      );
      const el = panel.current;
      // Full screen also needs the user gesture. If refused, the viewer still
      // works. Touch devices only: with a mouse it stays inside the window.
      if (
        el &&
        coarseRef.current &&
        document.fullscreenEnabled &&
        typeof el.requestFullscreen === "function"
      )
        void el.requestFullscreen({ navigationUI: "hide" })?.catch(() => {});
      const v = el?.querySelector("video");
      // If refused, the stage's onPlay never comes and it stays paused with ▶ (tap to play).
      void v?.play().catch(() => {});
    };
    window.addEventListener(VIDEO_VIEWER_EVENT, onOpen);
    return () => window.removeEventListener(VIDEO_VIEWER_EVENT, onOpen);
  }, []);

  // Touching the viewer's buttons brought up the keyboard on an iPhone: the
  // body (contenteditable) behind could take the focus although the viewer is
  // modal. So while open, every other child of body is made inert (a layout
  // effect, to apply before paint). Only the ones set here are cleared (ones
  // already inert are left alone). Focus that still escapes the panel is
  // pulled back on focusin.
  const isOpen = open !== null;
  // frontend-development: not a fetch; makes the page behind inert while open.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = panel.current;
    if (!el) return;
    const added: Element[] = [];
    for (const child of Array.from(document.body.children)) {
      if (child === el || child.contains(el) || child.hasAttribute("inert"))
        continue;
      child.setAttribute("inert", "");
      added.push(child);
    }
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target;
      if (!(t instanceof Node) || el.contains(t)) return;
      if (t instanceof HTMLElement) t.blur();
      el.focus({ preventScroll: true });
    };
    document.addEventListener("focusin", onFocusIn, true);
    return () => {
      document.removeEventListener("focusin", onFocusIn, true);
      for (const child of added) child.removeAttribute("inert");
    };
  }, [isOpen]);

  // Opening moves the focus to the panel; closing returns it to the opener.
  // frontend-development: not a fetch; moves the focus in and back out.
  useEffect(() => {
    if (!open) return;
    const el = panel.current;
    if (!el) return;
    if (!el.contains(document.activeElement)) el.focus({ preventScroll: true });
    return () => {
      // On touch devices, when the opener is inside the editable body, the focus
      // is not returned: focusing the editable area brings up the iOS keyboard
      // (user report: opening the viewer showed the keyboard). The focus stays
      // outside the body (on body).
      if (coarse && open.opener?.closest('[contenteditable="true"]')) {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body)
          active.blur();
        return;
      }
      returnFocusTo(el, open.opener);
    };
  }, [open, coarse]);

  // Escape when the focus is outside the panel (keys from inside are handled
  // on the panel and stopped there).
  // frontend-development: not a fetch; closes on Escape from outside the panel.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onEscape.current?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={open.name}
      tabIndex={-1}
      data-media-viewer=""
      style={{ zIndex: MEDIA_LAYER_Z }}
      className="fixed inset-0 overflow-hidden bg-black outline-none"
      // No iOS "Save" menu or text selection on a long press.
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onEscape.current?.();
        }
        // While the viewer is open, keys belong to it: the app's shortcuts
        // behind it must not run.
        e.stopPropagation();
      }}
    >
      <Stage
        key={open.src}
        open={open}
        panel={panel}
        onEscape={onEscape}
        onClose={close}
      />
    </div>,
    document.body,
  );
}

/** How long the controls stay before hiding (only while playing). */
export const CHROME_MS = 3000;
/** How long the "10 s" label of a double tap stays. */
const RIPPLE_MS = 700;

function Stage({
  open,
  panel,
  onEscape,
  onClose,
}: {
  open: Open;
  panel: RefObject<HTMLDivElement | null>;
  onEscape: RefObject<(() => void) | null>;
  onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [rotated, setRotated] = useState(false);
  const [chrome, setChrome] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(open.startAt);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [holding, setHolding] = useState(false);
  // The speed the user chose (not the 2x of a long press), and whether its list is open.
  const [rate, setRate] = useState(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [ripple, setRipple] = useState<{
    side: "back" | "forward";
    total: number;
    n: number;
  } | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  // iPhone: no element full screen, only the video's webkitEnterFullscreen.
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  // Whether full screen was ever entered (a "left" notice without entering does not close).
  const enteredFullscreen = useRef(false);
  const viewWidth = useRef(0);
  const gestures = useRef<VideoGestures | null>(null);
  gestures.current ??= new VideoGestures(() => viewWidth.current);
  const tickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chromeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rippleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only the first finger is followed (move/up/cancel of a second are ignored).
  const activePointer = useRef<number | null>(null);
  const hold = useRef<{ rate: number; paused: boolean } | null>(null);
  // The position handed back on close. Closing before or after a failed load
  // does not reset it to 0 (it starts at startAt).
  const position = useRef(open.startAt);
  const moreOpenRef = useRef(moreOpen);
  moreOpenRef.current = moreOpen;
  const speedOpenRef = useRef(speedOpen);
  speedOpenRef.current = speedOpen;
  // Whether the controls are shown (read from timers, hence a ref).
  const chromeRef = useRef(chrome);
  chromeRef.current = chrome;
  // Whether the controls were shown when the finger went down. A tap settles
  // 250 ms later; if they auto-hid meanwhile, it was still a press on shown controls.
  const chromeAtDown = useRef(true);
  // The pending tap's "shown at its press". A tap settled by the next finger
  // uses the previous finger's value.
  const pendingTapChrome = useRef<boolean | null>(null);
  // play/pause notices caused by a long press. They arrive asynchronously, even
  // after release. A long press does not change whether the controls show (user
  // request: coming back from 2x must not bring up the controls), so each one
  // it caused is ignored once.
  const holdEcho = useRef({ play: false, pause: false });
  const speedButton = useRef<HTMLButtonElement>(null);
  const showChromeRef = useRef<() => void>(() => {});
  const moreButton = useRef<HTMLButtonElement>(null);

  const finish = useCallback(() => {
    // Closing by ourselves leaves full screen too (the flag goes down first, so
    // the "left" notice does not close twice).
    const fs = document.fullscreenElement;
    if (fs && panel.current?.contains(fs)) {
      enteredFullscreen.current = false;
      void document.exitFullscreen?.()?.catch(() => {});
    }
    open.onClose?.(position.current);
    onClose();
  }, [open, onClose, panel]);

  // Escape: a list that is open closes first, else the viewer.
  onEscape.current = () => {
    if (moreOpenRef.current) {
      setMoreOpen(false);
      moreButton.current?.focus({ preventScroll: true });
      return;
    }
    if (speedOpenRef.current) {
      setSpeedOpen(false);
      speedButton.current?.focus({ preventScroll: true });
      return;
    }
    finish();
  };

  // Entering full screen sets the flag; the system leaving it closes the viewer.
  // frontend-development: not a fetch; subscribes to full screen changes.
  useEffect(() => {
    const inside = () => {
      const fs = document.fullscreenElement;
      return !!fs && !!panel.current?.contains(fs);
    };
    if (inside()) enteredFullscreen.current = true;
    const onChange = () => {
      if (inside()) enteredFullscreen.current = true;
      else if (!document.fullscreenElement && enteredFullscreen.current) {
        enteredFullscreen.current = false;
        finish();
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [panel, finish]);

  // Whether to offer the native player button (only without element full screen).
  // frontend-development: not a fetch; detects the device's full screen support.
  useEffect(() => {
    const elementFs =
      document.fullscreenEnabled &&
      typeof panel.current?.requestFullscreen === "function";
    const v = video.current as WebkitVideo | null;
    const native = !elementFs && typeof v?.webkitEnterFullscreen === "function";
    setNativeFullscreen(native);
    if (!native || !v) return;
    // Back from the iOS native player: position and play state as they are,
    // with the controls shown (React has no prop for webkitendfullscreen).
    const onEnd = () => showChromeRef.current();
    v.addEventListener("webkitendfullscreen", onEnd);
    return () => v.removeEventListener("webkitendfullscreen", onEnd);
  }, [panel]);

  // The panel's size (swapped onto the stage when turned).
  // frontend-development: not a fetch; observes the panel's size.
  useEffect(() => {
    const el = panel.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ width: r.width, height: r.height });
    };
    measure();
    if (typeof ResizeObserver !== "function") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [panel]);

  // frontend-development: not a fetch; cleanup of the timers.
  useEffect(
    () => () => {
      for (const t of [tickTimer, chromeTimer, rippleTimer])
        if (t.current) clearTimeout(t.current);
    },
    [],
  );

  const showChrome = () => {
    setChrome(true);
    if (chromeTimer.current) clearTimeout(chromeTimer.current);
    chromeTimer.current = setTimeout(() => {
      // Not while the speed or "…" list is open (user request; hiding would
      // make them inert and lose the focus).
      if (
        video.current &&
        !video.current.paused &&
        !moreOpenRef.current &&
        !speedOpenRef.current
      )
        setChrome(false);
    }, CHROME_MS);
  };

  showChromeRef.current = showChrome;

  /** Playing starts: the controls go at once (not while a list is open). */
  const hideChrome = () => {
    if (chromeTimer.current) clearTimeout(chromeTimer.current);
    if (!moreOpenRef.current && !speedOpenRef.current) setChrome(false);
  };

  const play = () => {
    void video.current?.play().catch(() => setPlaying(false));
  };

  const apply = (outs: GestureOutput[], tapChrome = true) => {
    const v = video.current;
    for (const o of outs) {
      if (!v) break;
      // Anything but a tap (skip, long press, swipe) closes the lists.
      if (o.kind !== "tap") {
        if (speedOpenRef.current) setSpeedOpen(false);
        if (moreOpenRef.current) setMoreOpen(false);
      }
      if (o.kind === "tap") {
        // User request: with the controls hidden, one tap shows them; a tap
        // while they show pauses / plays. A tap outside an open list only
        // closes the list.
        if (speedOpenRef.current || moreOpenRef.current) {
          setSpeedOpen(false);
          setMoreOpen(false);
        } else if (tapChrome) {
          if (v.paused) play();
          else v.pause();
        }
        showChrome();
      } else if (o.kind === "seek") {
        const d = o.side === "forward" ? GESTURE.seekStep : -GESTURE.seekStep;
        v.currentTime = clampTime(v.currentTime + d, v.duration);
        position.current = v.currentTime;
        setRipple((r) => ({
          side: o.side,
          total: o.total,
          n: (r?.n ?? 0) + 1,
        }));
        if (rippleTimer.current) clearTimeout(rippleTimer.current);
        rippleTimer.current = setTimeout(() => setRipple(null), RIPPLE_MS);
      } else if (o.kind === "holdStart") {
        hold.current = { rate: v.playbackRate, paused: v.paused };
        v.playbackRate = 2;
        if (v.paused) {
          holdEcho.current.play = true;
          void v.play().catch(() => {
            holdEcho.current.play = false;
            setPlaying(false);
          });
        }
        setHolding(true);
      } else if (o.kind === "holdEnd") {
        if (hold.current) {
          v.playbackRate = hold.current.rate;
          if (hold.current.paused && !v.paused) {
            holdEcho.current.pause = true;
            v.pause();
          }
        }
        hold.current = null;
        setHolding(false);
      } else if (o.dir === "up") {
        setRotated((r) => !r);
      } else {
        finish();
        return;
      }
    }
    // Set the timer again when something waits.
    if (tickTimer.current) clearTimeout(tickTimer.current);
    const at = gestures.current?.deadline();
    if (at == null) return;
    tickTimer.current = setTimeout(
      () => {
        const outs = gestures.current?.tick(performance.now()) ?? [];
        // A tap settled by time is the one that was waiting.
        const chromeOfTap = pendingTapChrome.current ?? true;
        if (outs.some((o) => o.kind === "tap")) pendingTapChrome.current = null;
        apply(outs, chromeOfTap);
      },
      Math.max(0, at - performance.now()),
    );
  };

  const point = (e: ReactPointerEvent) => {
    const r = panel.current?.getBoundingClientRect() ?? {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    };
    const p = toViewPoint(e.clientX, e.clientY, r, rotated);
    viewWidth.current = p.width;
    return { x: p.x, y: p.y, t: performance.now() };
  };
  /** Presses on buttons, the seek bar and menus are not gestures. */
  const onControl = (e: ReactPointerEvent) =>
    e.target instanceof Element &&
    e.target.closest("button, a, [data-seekbar], [role=menu]") !== null;

  // Both states use the same set of properties so turning and turning back
  // animate with a transition (user request). Before measuring (0) it is
  // inset 0, so the first render is not 0×0.
  const measured = size.width > 0 && size.height > 0;
  const stageStyle: CSSProperties = measured
    ? {
        width: rotated ? size.height : size.width,
        height: rotated ? size.width : size.height,
        top: "50%",
        left: "50%",
        transform: `translate(-50%, -50%) rotate(${rotated ? 90 : 0}deg)`,
      }
    : { inset: 0 };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the stage is the surface for finger gestures; mousedown only keeps the focus where it is
    <div
      data-viewer-stage=""
      className="absolute touch-none select-none transition-[transform,width,height] duration-[250ms] ease-out motion-reduce:transition-none [-webkit-touch-callout:none]"
      style={stageStyle}
      // A tap does not move the focus to the body behind (iPhone keyboard).
      // Gestures come from pointer events; mousedown's default is not needed.
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        if (onControl(e) || activePointer.current !== null) return;
        activePointer.current = e.pointerId;
        if (typeof e.currentTarget.setPointerCapture === "function")
          e.currentTarget.setPointerCapture(e.pointerId);
        apply(gestures.current?.down(point(e)) ?? []);
        chromeAtDown.current = chromeRef.current;
      }}
      onPointerMove={(e) => {
        // A mouse moving over it brings the controls back for a while.
        if (e.pointerType === "mouse" && activePointer.current === null) {
          showChrome();
          return;
        }
        if (e.pointerId !== activePointer.current) return;
        apply(gestures.current?.move(point(e)) ?? []);
      }}
      onPointerUp={(e) => {
        if (e.pointerId !== activePointer.current) return;
        activePointer.current = null;
        // Hidden controls show at once, without waiting the 250 ms for the tap
        // to settle — but only for a press that can be a tap (not on releasing
        // a long press or a swipe).
        if (!chromeAtDown.current && gestures.current?.isTapCandidate(point(e)))
          showChrome();
        // down() returns nothing. A previous tap that did not become a double
        // tap settles in up(), judged with the previous finger's value
        // (pendingTapChrome); this finger's value waits for the next settle.
        // Each tap is judged by whether the controls showed at its own press.
        const prevChrome = pendingTapChrome.current ?? true;
        const outs = gestures.current?.up(point(e)) ?? [];
        pendingTapChrome.current = gestures.current?.hasPending
          ? chromeAtDown.current
          : null;
        apply(outs, prevChrome);
      }}
      onPointerCancel={(e) => {
        if (e.pointerId !== activePointer.current) return;
        activePointer.current = null;
        apply(gestures.current?.cancel() ?? []);
      }}
    >
      {/* biome-ignore lint/a11y/useMediaCaption: user-uploaded recordings have no caption source */}
      <video
        ref={video}
        src={open.src}
        playsInline
        preload="auto"
        className="pointer-events-none absolute inset-0 h-full w-full object-contain"
        // Playback was started inside the opening tap (VideoViewer); only the position is set here.
        onLoadedMetadata={(e) => {
          const el = e.currentTarget;
          setDuration(el.duration);
          if (open.startAt > 0)
            el.currentTime = clampTime(open.startAt, el.duration);
        }}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => {
          position.current = e.currentTarget.currentTime;
          setTime(e.currentTarget.currentTime);
        }}
        onProgress={(e) => {
          const b = e.currentTarget.buffered;
          setBuffered(b.length > 0 ? b.end(b.length - 1) : 0);
        }}
        onPlay={() => {
          setPlaying(true);
          if (holdEcho.current.play) holdEcho.current.play = false;
          else hideChrome();
        }}
        onPause={() => {
          setPlaying(false);
          if (holdEcho.current.pause) holdEcho.current.pause = false;
          else setChrome(true);
        }}
      />
      {holding && (
        <span className="pointer-events-none absolute left-1/2 top-[max(16px,env(safe-area-inset-top))] -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-[13px] text-white">
          2x ▶▶
        </span>
      )}
      {ripple && (
        <span
          key={ripple.n}
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full bg-white/20 px-4 py-2 text-[14px] text-white ${ripple.side === "forward" ? "right-8" : "left-8"}`}
        >
          {ripple.side === "forward"
            ? `▶▶ ${m.media_video_seconds({ n: ripple.total })}`
            : `◀◀ ${m.media_video_seconds({ n: ripple.total })}`}
        </span>
      )}
      {/* The controls; they turn with the stage. Hidden ones can't be pressed or reached by Tab. */}
      <div
        data-viewer-controls=""
        inert={!chrome || undefined}
        className={`pointer-events-none absolute inset-0 transition-opacity duration-200 ease-out motion-reduce:transition-none ${chrome ? "[&_[data-seekbar]]:pointer-events-auto [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_[role=menu]]:pointer-events-auto" : "opacity-0"}`}
      >
        {/* Play / pause in the middle (user request). A tap on the stage
            waits 250 ms for a second one; this button acts at once. While
            paused the controls always show (onPause), so this ▶ is also the
            paused sign. During a long press the 2x label shows instead. */}
        {!holding && (
          <button
            type="button"
            data-testid="viewer-toggle"
            aria-label={playing ? m.media_video_pause() : m.media_video_play()}
            className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white"
            onClick={() => {
              const v = video.current;
              if (!v) return;
              if (v.paused) play();
              else v.pause();
              showChrome();
            }}
          >
            {playing ? <PauseIcon size={32} /> : <PlayIcon size={32} />}
          </button>
        )}
        <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 px-3 pt-[max(12px,env(safe-area-inset-top))]">
          <div className="min-w-0 truncate rounded-full bg-black/50 px-4 py-2 text-[13.5px] text-white">
            {open.name}
          </div>
          {/* "…" and × at the top. */}
          <div className="flex flex-none items-center gap-2">
            {open.download && (
              <div className="relative">
                <FloatingIconButton
                  ref={moreButton}
                  label={m.media_more_actions()}
                  hasPopup="menu"
                  expanded={moreOpen}
                  onClick={() => {
                    setSpeedOpen(false);
                    setMoreOpen((o) => !o);
                    showChrome();
                  }}
                >
                  <Icon kind="more" size={20} />
                </FloatingIconButton>
                {moreOpen && (
                  <div
                    role="menu"
                    aria-label={m.media_more_actions()}
                    className="absolute right-0 top-full mt-2 flex min-w-40 flex-col rounded-[10px] bg-black/85 py-1"
                  >
                    <a
                      role="menuitem"
                      href={open.download}
                      download=""
                      className="flex min-h-11 items-center px-4 text-left text-[14px] text-white"
                      onClick={() => {
                        setMoreOpen(false);
                        showChrome();
                        moreButton.current?.focus({ preventScroll: true });
                      }}
                    >
                      {m.media_download()}
                    </a>
                  </div>
                )}
              </div>
            )}
            <FloatingIconButton label={m.media_close()} onClick={finish}>
              <Icon kind="close" size={20} />
            </FloatingIconButton>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/60 to-transparent px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-8 text-white">
          <span className="text-[12px] tabular-nums">
            {formatVideoTime(time)}
          </span>
          <SeekBar
            time={time}
            duration={duration}
            buffered={buffered}
            rotated={rotated}
            onSeek={(t) => {
              position.current = t;
              if (video.current) video.current.currentTime = t;
            }}
          />
          <span className="text-[12px] tabular-nums">
            {formatVideoTime(duration)}
          </span>
          {nativeFullscreen && (
            <button
              type="button"
              aria-label={m.media_video_native_fullscreen()}
              className="flex min-h-11 min-w-11 flex-none items-center justify-center rounded-full text-white"
              onClick={() => {
                // Called synchronously inside the tap (an iOS requirement).
                (
                  video.current as WebkitVideo | null
                )?.webkitEnterFullscreen?.();
              }}
            >
              <FullscreenIcon />
            </button>
          )}
          {/* Playback speed (user request). Drawn inside, not portaled: it turns with the stage. */}
          <div className="relative flex-none">
            <button
              type="button"
              ref={speedButton}
              aria-label={m.media_video_speed()}
              aria-haspopup="menu"
              aria-expanded={speedOpen}
              className="flex min-h-11 min-w-11 items-center justify-center rounded-full px-2 text-[13px] tabular-nums text-white"
              onClick={() => {
                setMoreOpen(false);
                setSpeedOpen((o) => !o);
                showChrome();
              }}
            >
              {`${rate}x`}
            </button>
            {speedOpen && (
              <div
                role="menu"
                aria-label={m.media_video_speed()}
                // A turned stage is short (the device's width): scroll when the list does not fit.
                style={{
                  maxHeight: Math.max(
                    132,
                    (rotated ? size.width : size.height) - 120,
                  ),
                }}
                className="absolute bottom-full right-0 mb-2 flex min-w-28 flex-col overflow-y-auto rounded-[10px] bg-black/85 py-1"
              >
                {VIDEO_SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="menuitemradio"
                    aria-checked={s === rate}
                    // The chosen speed is shown by a background, not bold.
                    className={`flex min-h-11 items-center px-4 text-left text-[14px] text-white ${s === rate ? "bg-white/15" : ""}`}
                    onClick={() => {
                      if (video.current) video.current.playbackRate = s;
                      // Never chosen during a long press, but keep its return speed in step.
                      if (hold.current) hold.current.rate = s;
                      setRate(s);
                      setSpeedOpen(false);
                      showChrome();
                      speedButton.current?.focus({ preventScroll: true });
                    }}
                  >
                    {s === 1 ? m.media_video_speed_normal() : `${s}x`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
